// @ts-strict
import {type ReactElement} from 'react';
import {Button} from '@ezcater/tapas';
import {useFormikContext} from 'formik';
import useTranslation from 'next-translate/useTranslation';
import {twMerge} from 'tailwind-merge';

import useTracking from '@/hooks/useTracking';
import {useSupportMessageWizard} from './SupportMessageProvider';
import {StepEnum, type WebToCaseFormValues} from './types';
import {getStepFormikKeys} from './utils';

type SupportMessageActionsProps = {
  className?: string;
  onCancel?: () => void;
};

const SupportMessageActions = ({className, onCancel}: SupportMessageActionsProps): ReactElement => {
  const {t} = useTranslation('common');
  const {trackClick} = useTracking();
  const {step, setStep, isSingleStep, trackingContext} = useSupportMessageWizard();
  const {isSubmitting, submitForm, validateForm, setFieldTouched} =
    useFormikContext<WebToCaseFormValues>();
  const isFirstStep = step === StepEnum.ContactAndCategory;

  const cancelButton = onCancel && (
    <Button
      className="whitespace-nowrap"
      data-testid="support-message-cancel"
      onClick={onCancel}
      type="button"
      variant="outlined"
    >
      {t('components.SupportMessage.cancel')}
    </Button>
  );

  const nextButton = (
    <Button
      className="whitespace-nowrap"
      data-testid="support-message-next"
      onClick={async () => {
        // Step 1 must pass before advancing, or its errors would render on a step the user can no
        // longer see and submit would dead-end silently.
        const formErrors = await validateForm();
        const step1Keys = getStepFormikKeys(1);

        if (step1Keys.some(key => formErrors[key])) {
          step1Keys.forEach(key => setFieldTouched(key, true, false));
          return;
        }

        trackClick('support-message-next', {misc_json: JSON.stringify(trackingContext)});
        setStep(StepEnum.CategoryAndMessage);
      }}
      type="button"
    >
      {t('components.SupportMessage.next')}
    </Button>
  );

  const backButton = (
    <Button
      className="whitespace-nowrap"
      data-testid="support-message-back"
      onClick={() => {
        trackClick('support-message-back', {misc_json: JSON.stringify(trackingContext)});
        setStep(StepEnum.ContactAndCategory);
      }}
      type="button"
      variant="outlined"
    >
      {t('components.SupportMessage.back')}
    </Button>
  );

  const sendButton = (
    <Button
      className="whitespace-nowrap"
      data-testid="support-message-send"
      disabled={isSubmitting}
      onClick={submitForm}
      type="button"
    >
      {t('components.SupportMessage.send')}
    </Button>
  );

  return (
    <div
      className={twMerge(
        'flex flex-col gap-3 tablet:flex-row tablet:items-center tablet:justify-end',
        className,
      )}
    >
      {!isSingleStep && (
        <span className="text-sm text-peppercorn-500">
          {isFirstStep
            ? t('components.SupportMessage.step1Label')
            : t('components.SupportMessage.step2Label')}
        </span>
      )}
      <div className="flex justify-end gap-3">
        {isSingleStep ? (
          <>
            {cancelButton}
            {sendButton}
          </>
        ) : isFirstStep ? (
          <>
            {cancelButton}
            {nextButton}
          </>
        ) : (
          <>
            {backButton}
            {sendButton}
          </>
        )}
      </div>
    </div>
  );
};

export default SupportMessageActions;
